import { VuexModule, Action, Mutation } from 'vuex-module-decorators'
import * as T from '@/types'
import { CrudResource } from '../api/resources/api-crud'
import _find from 'lodash/find'
import _findIndex from 'lodash/findIndex'
import _difference from 'lodash/difference'
import Vue from 'vue'
import { GetterTree, ActionTree, MutationTree } from 'vuex'

export interface CrudState<Entity extends T.Entity> {
    items: Entity[]
    pagination: T.PaginationMetadata | null
    loading: boolean,
    loaded: boolean,
}

const mixin = function <Entity extends T.Entity> (resource: CrudResource<Entity>) {
    const state: () => CrudState<Entity> = () => {
        return {
            items: [],
            pagination: null,
            loading: false,
            loaded: false,
        }
    }

    const getters: GetterTree<CrudState<Entity>, T.Store.RootState> = {
        all: (state) => state.items,

        get: (state) => (ids: string | string[]) => {
            if (Array.isArray(ids)) {
                return state.items.filter((possible) => {
                    return ids.includes(possible.id)
                })
            }
            else if (typeof ids === 'string') {
                const id = ids
                return state.items.find((possible) => {
                    return possible.id === id
                })
            }
        },
        getMany: (state) => (ids: T.EntityId[]) => {
            return state.items.filter((possible) => {
                return ids.includes(possible.id)
            })
        },
        
        count: (state) => state.items.length,

        pagination: (state) => state.pagination,

        hasPrevious: (state) => {
            if (!state.pagination) return false
            return state.pagination.first !== state.pagination.self
        },

        hasNext: (state) => {
            if (!state.pagination || !state.pagination.next) return false
            return state.pagination.last !== state.pagination.self
        },

        totalResults: (state) => state.pagination && state.pagination.totalResults,
        totalPages: (state) => state.pagination && state.pagination.totalPages,

        loading: (state) => state.loading,
    }

    const actions: ActionTree<CrudState<Entity>, T.Store.RootState> = {
        async refresh ({ commit }, filters? : T.Filter[]) {
            commit('setLoading', true)
            try {
                const items = await resource.all(filters)
                commit('setItems', items)
            } catch (error) {
                console.log('Error in store-crud:', error)
            } finally {
                commit('setLoading', false)

            }
        },
        async loadMore ({ commit, getters }, filters? : T.Filter[]) {
            commit('setLoading', true)
            const pageRoute = getters.pagination.next
            const prevItems = getters.all
            
            try {
                const items = await resource.page(pageRoute)
                const newItems = {
                    items: [
                        ...prevItems,
                        ...items.items,
                    ],
                    meta: items.meta,
                }
                commit('setItems', newItems)
            } catch (error) {
                console.log('Error loading more:', error)
            } finally {
                commit('setLoading', false)
            }
            
        },
        save ({ commit, getters } , item : Entity) {
            return resource.upsert(item)
                .then((entity: Entity) => {
                    // check if existing and update
                    const existing = _find(getters.all, (possible) => {
                        return possible.id === entity.id
                    })
    
                    if (existing) {
                        commit('update', entity)
                    } else {
                        commit('insert', entity)
                    }
    
                    return entity
                })
        },
        saveMany ({ commit, getters }, items: Entity[]) {
            return Promise.all(items.map((item) => {
                return resource.upsert(item)
            })).then((items: Entity[]) => {
                items.forEach((entity) => {
                    // check if existing and update
                    const existing = _find(getters.all, (possible) => {
                        return possible.id === entity.id
                    })
    
                    if (existing) {
                        commit('update', entity)
                    } else {
                        commit('insert', entity)
                    }
    
                    return entity
                })
            })
        },
        // archive ({ commit }, entity : Entity) {
        //     return resource.archive(entity).then((entity: Entity) => {
        //         commit('update', entity)
        //     })
        // },
        // unarchive ({ commit }, entity: Entity) {
        //     return resource.unarchive(entity).then((entity: Entity) => {
        //         commit('update', entity)
        //     })
        // },
    }

    const mutations: MutationTree<CrudState<Entity>> = {
        setItems (state, paginator : T.Paginator<Entity>) {
            state.items = paginator.items
            state.pagination = paginator.meta
        },
        insert (state, entity: Entity) {
            state.items.unshift(entity)
        },
        update (state, entity: Entity) {
            let index = _findIndex(state.items, (possible) => {
                return possible.id == entity.id
            })

            if (index > -1) {
                Vue.set(state.items, index, entity)
            }
        },
        delete (state, entity: Entity) {
            let index = _findIndex(state.items, (possible) => {
                return possible.id == entity.id
            })

            if (index > -1) {
                Vue.delete(state.items, index)
            }
        },
        setLoading (state, loading: boolean) {
            loading ? state.loaded = false : state.loaded = true
            state.loading = loading
        },
    }

    return {
        namespaced: true,
        state: state(),
        getters,
        actions,
        mutations,
    }
}

export default mixin
